<template>
  <section class="cms-access-wall">
    <h1 class="content-title">
      {{ title }}
    </h1>

    <div class="wall">
      <i class="fa fa-lock wall-icon" />
      <h2 class="wall-title">
        {{ $gadTranslate(copy.title) }}
      </h2>
      <p class="wall-body">
        {{ $gadTranslate(copy.body) }}
      </p>
      <BButton
        v-if="reason === CMS_ACCESS_DENIED.LOGIN_REQUIRED"
        variant="primary"
        :to="loginPath"
      >
        {{ $gadTranslateUpper('general_button_log_in') }}
      </BButton>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { CMS_ACCESS_DENIED } from 'give-a-day-shared/data/enums/cmsContentEnums.mjs'

// Shown in place of the cover image, author and body of a piece of Private Content the visitor may not
// read. The title is all the backend sends for such a post, and all this renders of it: it says what
// the reader is being asked to log in for, without disclosing a word of the article.
const props = defineProps({
  title: { type: String, required: true },
  reason: { type: String, required: true },
})

const nuxtApp = useNuxtApp()
const route = useRoute()

const COPY_BY_REASON = {
  [CMS_ACCESS_DENIED.LOGIN_REQUIRED]: { title: 'cms_private_login_required_title', body: 'cms_private_login_required_body' },
  [CMS_ACCESS_DENIED.NOT_LINKED]: { title: 'cms_private_not_member_title', body: 'cms_private_not_member_body' },
}

const copy = computed(() => COPY_BY_REASON[props.reason] ?? COPY_BY_REASON[CMS_ACCESS_DENIED.NOT_LINKED])

// The login screen already honours redirectUrl, so logging in from here finishes the job the reader
// started and lands them back on the article. A visitor who is logged in but is not a member gets no
// button at all: there is nothing useful to send them to.
// Deliberately NOT a computed: a computed reading route.fullPath subscribes its ReactiveEffect to the
// reactive route proxy, which on the server pins the ssrContext (and its H3Event) in memory after the
// request ends — the same leak previously fixed in PageMeta.vue and OneDayVacancySubscriptionModal.vue.
// A plain read in setup registers no dependency, and the wall remounts on navigation anyway.
const loginPath = nuxtApp.$findPath('/login', null, null, { redirectUrl: route.fullPath })
</script>

<style lang="scss" scoped>
@import '~assets/styles/variables/colors';

.cms-access-wall {
  max-width: 800px;
  width: 100%;
  padding: 0 30px;
  margin: 0 auto;

  .content-title {
    margin: 0 0 24px;
    // On a platform, the title takes that platform's primary colour, exactly as it does on a post the
    // reader is allowed to open. The variable is only defined on platform pages.
    color: var(--primary-color);
  }

  .wall {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
    padding: 48px 24px;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
  }

  .wall-icon {
    font-size: 2rem;
    color: $secondary;
  }

  .wall-title {
    margin: 0;
    font-size: 1.4rem;
  }

  .wall-body {
    margin: 0;
    max-width: 480px;
    color: $secondary;
  }
}
</style>
